import { EditOutlined } from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { Drawer } from "components/Drawer/Drawer"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import { useNotification } from "components/Notification/Notification"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useDisclosure } from "hooks/useDisclosure"
import {
	AdminCareOrganization,
	AdminCareOrganizationUpdatePayload,
} from "interface/careOrganization"
import { useCallback, useMemo } from "react"
import { useAdminCareOrganizationUpdate } from "services/organizations"
import { DEFAULT_TIMEZONE, GENERIC_ERROR_MESSAGE } from "utils/constants"
import { CareOrganizationFormFields } from "./CareOrganizationFormFields"
import { CareOrganizationProviders } from "./CareOrganizationProviders"

export function UpdateCareOrganization({
	careOrganization,
}: {
	careOrganization: AdminCareOrganization
}) {
	const disclose = useDisclosure()
	const title = `Edit care organization ${careOrganization.name}`

	return (
		<>
			<Tooltip.Dark title={title}>
				<Button
					type="text"
					size="large"
					icon={<EditOutlined aria-hidden />}
					aria-label={title}
					onClick={disclose.open}
				/>
			</Tooltip.Dark>

			{disclose.isOpen && (
				<UpdateCareOrganizationForm
					careOrganization={careOrganization}
					onClose={disclose.close}
				/>
			)}
		</>
	)
}

function UpdateCareOrganizationForm({
	careOrganization,
	onClose,
}: {
	careOrganization: AdminCareOrganization
	onClose: VoidFunction
}) {
	const { notify } = useNotification()
	const [form] = Form.useForm<FormValues>()
	const update = useAdminCareOrganizationUpdate(careOrganization.id)
	const title = "Edit care organization"
	const initialValues = useMemo<FormValues>(
		() => ({
			name: careOrganization.name,
			timezone: careOrganization.timezone ?? DEFAULT_TIMEZONE,
			excludedFromAnalytics: careOrganization.excludedFromAnalytics,
			supervisorId: careOrganization.supervisorId,
		}),
		[careOrganization],
	)
	const handleCancel = useCallback(() => {
		if (update.isPending) return
		onClose()
	}, [onClose, update.isPending])
	const handleFinish = useCallback(
		(values: FormValues) => {
			const name = values.name.trim()

			update.mutate(
				{ ...values, name },
				{
					onSuccess: () => {
						notify.success({
							message: title,
							description: `${name} was updated successfully`,
						})
						onClose()
					},
				},
			)
		},
		[notify, onClose, update],
	)

	return (
		<Drawer
			open
			aria-label={title}
			title={
				<BackButton disabled={update.isPending} onClick={handleCancel}>
					Care organizations
				</BackButton>
			}
			rootClassName="z-10"
			onClose={handleCancel}
		>
			<h2 className="font-headings text-3xl font-semibold">{title}</h2>
			<div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
				<Form<FormValues>
					form={form}
					autoComplete="off"
					name="admin-care-organization-update"
					layout="vertical"
					aria-label={title}
					initialValues={initialValues}
					className="shrink-0 rounded-2xl bg-mint-0 p-2 xs:p-4"
					onFinish={handleFinish}
				>
					<CareOrganizationFormFields
						careOrganizationId={careOrganization.id}
					/>
					<Form.Item className="flex flex-col gap-2 pt-2">
						<Button
							type="primary"
							htmlType="button"
							loading={update.isPending}
							className="w-full"
							onClick={form.submit}
						>
							Confirm
						</Button>
						<ErrorStatus show={update.isError}>
							{update.error?.message ?? GENERIC_ERROR_MESSAGE}
						</ErrorStatus>
					</Form.Item>
				</Form>
				<CareOrganizationProviders careOrganizationId={careOrganization.id} />
			</div>
		</Drawer>
	)
}

type FormValues = Required<Pick<AdminCareOrganizationUpdatePayload, "name">> &
	Partial<Omit<AdminCareOrganizationUpdatePayload, "name">>
